import { AddAPhotoOutlined } from "@mui/icons-material";
import { Box, Button, Typography, Alert } from "@mui/material";
import { assignUnit, getWaitingList } from "actions/dashboard";
import { Modal } from "components";
import { FormField } from "empower-energy-owner-components";
import { isEmpty } from "lodash";
import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import CameraCaptureModal from "./CameraCaptureModal";
import { clearOcr, uploadOcrDocument } from "actions/ocr";
import { dataURLtoFile } from "utils/wizard";
import { LoadingButton } from "@mui/lab";
import sample from "images/dashboard/electrobank-code-sample.png";

const styles = () => {
  return {
    height: { xs: "100%", sm: "calc(100vh - 140px)" },
    mt: 1,
    "& .mui-field": {
      display: "block",
      "& .MuiFormGroup-root": {
        justifyContent: "center",
        "& .MuiFormControlLabel-root": {
          width: "200px",
        },
      },
    },
    "& .camera-section": {
      my: 1,
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
    },
    "& .button-footer": {
      pt: 1,
      display: "flex",
      alignItems: "center",
      justifyContent: "end",
      borderTop: "1px solid #ccc",
    },
  };
};

function extractUploadMessage(response) {
  if (!response || !response.status) {
    return "Unexpected error occurred.";
  }

  const { status, errors } = response;

  if (status === "assigned") {
    return "";
  }

  if (status === "low_confidence") {
    return "Incomplete details extracted from photo. Please review or retake the picture.";
  }

  if (status === "extraction_failed") {
    return "Photo extraction failed. Please retake the picture.";
  }

  if (status === "validation_failed" && errors) {
    const messages = Object.entries(errors)
      .map(([field, errs]) => `${field}: ${errs.join(", ")}`)
      .join(" | ");

    return `Validation failed — ${messages}`;
  }

  return "Unexpected error occurred.";
}

function extractUploadSerialPassword(response) {
  if (!response || !response.status) {
    return {};
  }

  const { serial, password } = response;
  return { serial, password };
}

const AddUnitModal = ({ open, onClose, onAdd }) => {
  const dispatch = useDispatch();

  const [formData, setFormData] = useState({
    serial: "",
    password: "",
  });

  const [errors, setErrors] = useState({
    serial: "",
    password: "",
  });

  const [cameraOpen, setCameraOpen] = useState(false);
  const [capturedImage, setCapturedImage] = useState(null);

  const [ocrError, setOcrError] = useState("");

  const addLoading = useSelector((state) => state.dashboard?.addLoading);

  const ocrLoading = useSelector((state) => state.ocr?.loading);
  const uploadDetails = useSelector((state) => state.ocr?.uploadDetails);

  useEffect(() => {
    dispatch(clearOcr());
  }, [dispatch]);

  useEffect(() => {
    if (uploadDetails) {
      const error = extractUploadMessage(uploadDetails);
      const fieldData = extractUploadSerialPassword(uploadDetails);
      setOcrError(error);
      setFormData(fieldData);
    } else {
      setOcrError("");
    }
  }, [uploadDetails]);

  const onChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
    onBlur(e);
  };

  const onBlur = (e) => {
    const newErrors = {
      ...errors,
      [e.target.name]: isEmpty(e.target.value) ? "Required" : "",
    };
    setErrors(newErrors);

    if (e.target.name === "password" && e.target.value.length !== 16) {
      setErrors({
        ...errors,
        [e.target.name]: "Password should be 16 characters long",
      });
    }

    if (e.target.name === "serial" && e.target.value.length !== 12) {
      setErrors({
        ...errors,
        [e.target.name]: "Serial should be 12 characters long",
      });
    }
  };

  const onClickAdd = () => {
    dispatch(
      assignUnit(formData, () => {
        onClose(false);
        onAdd();
      })
    );
  };

  const isDisabled = () =>
    Object.values(formData).every((it) => isEmpty(it)) ||
    !Object.values(errors).every((it) => isEmpty(it));

  const onChangeCapture = async (img) => {
    setOcrError("");
    setCapturedImage(img);
    const file = dataURLtoFile(img, "label.jpg");
    const ocrResponse = await dispatch(uploadOcrDocument(file));
    if (ocrResponse?.status === "assigned") {
      onClose();
      onAdd();
      dispatch(getWaitingList());
    }
  };

  return (
    <>
      <Modal
        width="400px"
        maxHeight="98%"
        top="10%"
        title="Enter Elektrobank Details"
        open={open}
        onClose={onClose}
        showHeaderBorder={true}
      >
        <Box sx={styles}>
          <Box mb={1}>
            <Typography variant="subtitle2" ml={1}>
              {capturedImage ? "Your Captured Label" : "Sample Image"}
            </Typography>
            <Box className="items-justify-center">
              {capturedImage ? (
                <img
                  src={capturedImage}
                  alt="Captured"
                  style={{ borderRadius: 4 }}
                  width="340px"
                />
              ) : (
                <img
                  src={sample}
                  alt="Captured"
                  style={{ borderRadius: 4 }}
                  width="340px"
                />
              )}
            </Box>
          </Box>

          {!isEmpty(ocrError) && (
            <Alert severity="error" sx={{ mb: 1 }}>
              <Typography fontSize={"12px"}>{ocrError}</Typography>
            </Alert>
          )}

          {ocrLoading && (
            <Alert severity="info" sx={{ mb: 1 }}>
              <Typography fontSize={"12px"}>Processing your request</Typography>
            </Alert>
          )}

          <FormField
            label="Serial"
            name="serial"
            value={formData.serial}
            variant="filled"
            onChange={onChange}
            autoComplete="off"
            error={errors.serial}
            onBlur={onBlur}
            disabled={addLoading}
          />
          <FormField
            label="Password"
            name="password"
            type="password"
            value={formData.password}
            variant="filled"
            onChange={onChange}
            autoComplete="new-password"
            error={errors.password}
            onBlur={onBlur}
            disabled={addLoading}
          />

          <Box className="camera-section">
            <Typography variant="subtitle2" letterSpacing={1}>
              OR take a picture of the label on battery door.
            </Typography>
            <LoadingButton
              variant="outlined"
              startIcon={<AddAPhotoOutlined />}
              sx={{ mt: 0.5 }}
              onClick={() => setCameraOpen(true)}
              loading={ocrLoading}
            >
              Input with Camera
            </LoadingButton>
          </Box>

          <Box className="button-footer">
            <Button onClick={onClose}>Cancel</Button>
            <Button onClick={onClickAdd} disabled={addLoading || isDisabled()}>
              Add
            </Button>
          </Box>
        </Box>
      </Modal>

      {cameraOpen && (
        <CameraCaptureModal
          open={cameraOpen}
          onClose={() => setCameraOpen(false)}
          image={capturedImage}
          onChange={onChangeCapture}
        />
      )}
    </>
  );
};

export default AddUnitModal;
